import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  subscribeToApprovalRequests,
  subscribeToEffectReceipts,
  subscribeToFutureActionCommitments,
  subscribeToInteractionPrimitives,
} from '../services/zeroEffortRepo';
import {
  buildAssistantSurfaceOpenedPayload,
  isAssistantSurfaceOpenedEnabled,
  notifyAssistantSurfaceOpened,
} from '../services/assistantClient';
import { selectInteractionPrimitiveView } from '../models/interactionPrimitiveModel';

const SOURCE_KEYS = [
  'interactionPrimitives',
  'approvalRequests',
  'effectReceipts',
  'futureActionCommitments',
];
const SURFACE_OPEN_DEBOUNCE_MS = 600;
const SURFACE_OPEN_RETRY_MS = 3000;
const MAX_SURFACE_OPEN_RETRIES = 1;

function isTransientSurfaceOpenDegradation(error) {
  return error?.details?.status === 'stale_context';
}

const initialState = {
  interactionPrimitives: [],
  approvalRequests: [],
  effectReceipts: [],
  futureActionCommitments: [],
  loading: false,
  error: null,
  surfaceOpenError: null,
  loaded: {
    interactionPrimitives: false,
    approvalRequests: false,
    effectReceipts: false,
    futureActionCommitments: false,
  },
};

function loadedStateForSources(activeSources) {
  const active = new Set(activeSources);
  return Object.fromEntries(
    SOURCE_KEYS.map((key) => [key, !active.has(key)])
  );
}

function reducer(state, action) {
  switch (action.type) {
    case 'START':
      const loaded = loadedStateForSources(action.sources || SOURCE_KEYS);
      return {
        ...initialState,
        loaded,
        loading: SOURCE_KEYS.some((key) => !loaded[key]),
      };

    case 'RESET':
      return initialState;

    case 'UPDATE': {
      const loaded = {
        ...state.loaded,
        [action.key]: true,
      };

      return {
        ...state,
        [action.key]: action.docs,
        loaded,
        loading: SOURCE_KEYS.some((key) => !loaded[key]),
      };
    }

    case 'ERROR':
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    case 'SURFACE_ERROR':
      return {
        ...state,
        surfaceOpenError: action.error,
      };

    case 'SURFACE_OK':
      return {
        ...state,
        surfaceOpenError: null,
      };

    default:
      return state;
  }
}

/**
 * Subscribe to the Zero-effort inbox artifacts independently from chat messages.
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {string} [options.userId]
 * @param {string} [options.locale]
 * @param {string} [options.timezone]
 * @param {string} [options.surfaceContextKey]
 * @returns {Object}
 */
export function useZeroEffortInbox({
  enabled = true,
  userId,
  locale,
  timezone,
  surfaceContextKey,
} = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const triggerTimerRef = useRef(0);
  const retryTimerRef = useRef(0);
  const triggerInFlightRef = useRef(false);
  const lastSuccessfulKeyRef = useRef(null);
  const pendingTriggerRef = useRef(null);
  const retryStateRef = useRef({ key: null, attempts: 0 });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const inboxArtifactsEnabled = enabled && Boolean(userId);
  const interactionSurfaceEnabled =
    inboxArtifactsEnabled && isAssistantSurfaceOpenedEnabled();

  useEffect(() => {
    lastSuccessfulKeyRef.current = null;
    pendingTriggerRef.current = null;
    retryStateRef.current = { key: null, attempts: 0 };
  }, [userId]);

  useEffect(() => {
    if (!inboxArtifactsEnabled) {
      lastSuccessfulKeyRef.current = null;
      pendingTriggerRef.current = null;
      retryStateRef.current = { key: null, attempts: 0 };
      if (triggerTimerRef.current) {
        clearTimeout(triggerTimerRef.current);
        triggerTimerRef.current = 0;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = 0;
      }
      dispatch({ type: 'RESET' });
      return undefined;
    }

    const sources = [
      ...(interactionSurfaceEnabled ? ['interactionPrimitives'] : []),
      'approvalRequests',
      'effectReceipts',
      'futureActionCommitments',
    ];
    dispatch({ type: 'START', sources });

    let unsubscribers = [];
    try {
      unsubscribers = [
        subscribeToApprovalRequests(
          userId,
          (docs) => dispatch({ type: 'UPDATE', key: 'approvalRequests', docs }),
          (error) => dispatch({ type: 'ERROR', error })
        ),
        subscribeToEffectReceipts(
          userId,
          (docs) => dispatch({ type: 'UPDATE', key: 'effectReceipts', docs }),
          (error) => dispatch({ type: 'ERROR', error })
        ),
        subscribeToFutureActionCommitments(
          userId,
          (docs) => dispatch({
            type: 'UPDATE',
            key: 'futureActionCommitments',
            docs,
          }),
          (error) => dispatch({ type: 'ERROR', error })
        ),
      ];
      if (interactionSurfaceEnabled) {
        unsubscribers.unshift(
          subscribeToInteractionPrimitives(
            userId,
            (docs) => dispatch({ type: 'UPDATE', key: 'interactionPrimitives', docs }),
            (error) => dispatch({ type: 'ERROR', error })
          )
        );
      }
    } catch (error) {
      console.error('Zero-effort inbox subscription error:', error);
      dispatch({ type: 'ERROR', error });
      return undefined;
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe?.());
    };
  }, [userId, inboxArtifactsEnabled, interactionSurfaceEnabled]);

  useEffect(() => {
    if (!interactionSurfaceEnabled) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      setNowMs(Date.now());
    }, 30000);
    return () => clearInterval(intervalId);
  }, [interactionSurfaceEnabled]);

  useEffect(() => {
    if (!interactionSurfaceEnabled) {
      return undefined;
    }

    const triggerKey = [
      userId,
      locale || '',
      timezone || '',
      surfaceContextKey || 'initial',
    ].join('|');
    if (lastSuccessfulKeyRef.current === triggerKey) {
      return undefined;
    }
    const triggerRequest = {
      key: triggerKey,
      payload: buildAssistantSurfaceOpenedPayload({ locale, timezone }),
    };

    if (triggerTimerRef.current) {
      clearTimeout(triggerTimerRef.current);
    }

    const runTrigger = (request) => {
      if (triggerInFlightRef.current) {
        pendingTriggerRef.current = request;
        return;
      }

      triggerInFlightRef.current = true;

      void notifyAssistantSurfaceOpened(request.payload)
        .then(() => {
          lastSuccessfulKeyRef.current = request.key;
          retryStateRef.current = { key: null, attempts: 0 };
          dispatch({ type: 'SURFACE_OK' });
        })
        .catch((error) => {
          if (isTransientSurfaceOpenDegradation(error)) {
            dispatch({ type: 'SURFACE_OK' });
          } else {
            dispatch({ type: 'SURFACE_ERROR', error });
          }
          const pending = pendingTriggerRef.current;
          const hasNewerPending = pending && pending.key !== request.key;
          const retryState = retryStateRef.current;
          const attempts = retryState.key === request.key ? retryState.attempts : 0;

          if (!hasNewerPending && attempts < MAX_SURFACE_OPEN_RETRIES) {
            retryStateRef.current = {
              key: request.key,
              attempts: attempts + 1,
            };
            if (retryTimerRef.current) {
              clearTimeout(retryTimerRef.current);
            }
            retryTimerRef.current = setTimeout(() => {
              retryTimerRef.current = 0;
              if (lastSuccessfulKeyRef.current !== request.key) {
                runTrigger(request);
              }
            }, SURFACE_OPEN_RETRY_MS);
          }
        })
        .finally(() => {
          triggerInFlightRef.current = false;
          const pending = pendingTriggerRef.current;
          pendingTriggerRef.current = null;

          if (pending && pending.key !== lastSuccessfulKeyRef.current) {
            if (retryTimerRef.current) {
              clearTimeout(retryTimerRef.current);
              retryTimerRef.current = 0;
            }
            runTrigger(pending);
          }
        });
    };

    triggerTimerRef.current = setTimeout(() => {
      if (triggerInFlightRef.current) {
        pendingTriggerRef.current = triggerRequest;
        return;
      }

      runTrigger(triggerRequest);
    }, SURFACE_OPEN_DEBOUNCE_MS);

    return () => {
      if (triggerTimerRef.current) {
        clearTimeout(triggerTimerRef.current);
        triggerTimerRef.current = 0;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = 0;
      }
    };
  }, [locale, timezone, userId, interactionSurfaceEnabled, surfaceContextKey]);

  const primitiveView = useMemo(
    () => selectInteractionPrimitiveView(state.interactionPrimitives, nowMs),
    [nowMs, state.interactionPrimitives]
  );

  return {
    interactionPrimitives: state.interactionPrimitives,
    starterPrimitives: primitiveView.starterPrimitives,
    replyPrimitivesByAnchorId: primitiveView.replyPrimitivesByAnchorId,
    approvalRequests: state.approvalRequests,
    effectReceipts: state.effectReceipts,
    futureActionCommitments: state.futureActionCommitments,
    artifactsLoaded:
      state.loaded.approvalRequests &&
      state.loaded.effectReceipts &&
      state.loaded.futureActionCommitments,
    loading: state.loading,
    error: state.error,
    surfaceOpenError: state.surfaceOpenError,
  };
}
